import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import ScrollArrow from '../components/ScrollArrow'
import GlareCard from '../components/ui/GlareCard'
import ProgressiveImage from '../components/ui/ProgressiveImage'
import { textReveal, staggerContainer, staggerItem } from '../animations/variants'

const Innovation = () => {
  const { t } = useLanguage()

  return (
    <div className="bg-black">
      {/* Hero Section - Full Screen */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/hero-innovation.jpg"
            placeholder="/images/placeholders/hero-innovation-placeholder.jpg"
            alt="AI Technology"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label">INNOVATION</motion.div>
            <motion.h1 variants={textReveal} className="section-heading text-glow">
              {t('innovation.hero.title')}
            </motion.h1>
            <motion.p variants={textReveal} className="section-description">
              {t('innovation.hero.subtitle')}
            </motion.p>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* Innovation 1 - 网格纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-grid-texture">
        {/* 顶部渐变过渡 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={staggerContainer}
              viewport={{ once: true }}
            >
              <motion.div variants={textReveal} className="section-label mb-6">{t('innovation.first.badge')}</motion.div>
              <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
                {t('innovation.first.title')}
              </motion.h2>

              <motion.div variants={staggerContainer} className="space-y-6">
                {[
                  { icon: 'memory', title: t('innovation.first.feature1.title'), desc: t('innovation.first.feature1.desc') },
                  { icon: 'check_circle', title: t('innovation.first.feature2.title'), desc: t('innovation.first.feature2.desc') },
                  { icon: 'bolt', title: t('innovation.first.feature3.title'), desc: t('innovation.first.feature3.desc') },
                  { icon: 'arrow_forward', title: t('innovation.first.feature4.title'), desc: t('innovation.first.feature4.desc') }
                ].map((item, index) => (
                  <motion.div key={index} variants={staggerItem} className="flex items-start gap-4">
                    <span className="material-symbols-outlined text-xl text-white/30 mt-1">{item.icon}</span>
                    <div>
                      <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-2">{item.title}</h3>
                      <p className="text-gray-500 text-sm font-mono">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={textReveal}
              viewport={{ once: true }}
              className="metal-surface p-8"
            >
              <div className="text-center">
                <span className="material-symbols-outlined text-5xl text-white/30 mb-6">memory</span>
                <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-widest">{t('innovation.first.visual.title')}</h3>
                <p className="text-gray-500 text-sm font-mono mb-6">{t('innovation.first.visual.subtitle')}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {[
                    { label: t('innovation.first.visual.accuracy'), value: t('innovation.first.visual.accuracy.value') },
                    { label: t('innovation.first.visual.coverage'), value: t('innovation.first.visual.coverage.value') },
                    { label: t('innovation.first.visual.arch'), value: t('innovation.first.visual.arch.value') },
                    { label: t('innovation.first.visual.integration'), value: t('innovation.first.visual.integration.value') }
                  ].map((item, index) => (
                    <div key={index} className="p-4 border border-white/10">
                      <span className="font-mono text-white/70">{item.label}</span>
                      <p className="text-gray-600 text-xs mt-1 font-mono">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Innovation 2 - 渐变纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-gradient-texture">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={textReveal}
              viewport={{ once: true }}
              className="order-2 lg:order-1 metal-surface p-8"
            >
              <div className="text-center">
                <span className="material-symbols-outlined text-4xl text-white/30 mb-6">psychology</span>
                <h3 className="text-lg font-bold text-white mb-3 uppercase tracking-widest">{t('innovation.second.visual.title')}</h3>
                <p className="text-gray-500 text-sm font-mono mb-8">{t('innovation.second.visual.subtitle')}</p>

                <div className="space-y-4">
                  <div className="p-4 border border-white/10 hover:border-white/30 transition-colors">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="material-symbols-outlined text-xl text-white/30">psychology</span>
                      <span className="font-bold text-white text-sm uppercase tracking-widest">{t('innovation.second.visual.design')}</span>
                    </div>
                    <p className="text-gray-500 text-sm font-mono">{t('innovation.second.visual.design.desc')}</p>
                  </div>

                  <div className="p-4 border border-white/10 hover:border-white/30 transition-colors">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="material-symbols-outlined text-xl text-white/30">menu_book</span>
                      <span className="font-bold text-white text-sm uppercase tracking-widest">{t('innovation.second.visual.knowledge')}</span>
                    </div>
                    <p className="text-gray-500 text-sm font-mono">{t('innovation.second.visual.knowledge.desc')}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={staggerContainer}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <motion.div variants={textReveal} className="section-label mb-6">{t('innovation.second.badge')}</motion.div>
              <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
                {t('innovation.second.title')}
              </motion.h2>

              <motion.div variants={staggerContainer} className="space-y-6 mb-8">
                {[
                  { icon: 'psychology', title: t('innovation.second.feature1.title'), desc: t('innovation.second.feature1.desc') },
                  { icon: 'bolt', title: t('innovation.second.feature2.title'), desc: t('innovation.second.feature2.desc') },
                  { icon: 'lightbulb', title: t('innovation.second.feature3.title'), desc: t('innovation.second.feature3.desc') }
                ].map((item, index) => (
                  <motion.div key={index} variants={staggerItem} className="flex items-start gap-4">
                    <span className="material-symbols-outlined text-xl text-white/30 mt-1">{item.icon}</span>
                    <div>
                      <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-2">{item.title}</h3>
                      <p className="text-gray-500 text-sm font-mono">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div variants={textReveal} className="metal-surface p-6">
                <h4 className="font-bold text-white mb-4 uppercase tracking-widest text-sm">{t('innovation.second.expansion.title')}</h4>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    t('innovation.second.expansion.power'),
                    t('innovation.second.expansion.chassis'),
                    t('innovation.second.expansion.body'),
                    t('innovation.second.expansion.electrical'),
                    t('innovation.second.expansion.interior'),
                    t('innovation.second.expansion.safety')
                  ].map((field, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-white/30 text-sm">check_circle</span>
                      <span className="text-xs text-gray-400 font-mono">{field}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Innovation Impact - 深灰背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-dark-gray">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">IMPACT</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('innovation.impact.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm max-w-3xl mx-auto">
              {t('innovation.impact.subtitle')}
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: 'bolt', title: t('innovation.impact.efficiency.title'), value: t('innovation.impact.efficiency.value'), desc: t('innovation.impact.efficiency.desc') },
              { icon: 'verified', title: t('innovation.impact.quality.title'), value: t('innovation.impact.quality.value'), desc: t('innovation.impact.quality.desc') },
              { icon: 'psychology', title: t('innovation.impact.ai.title'), value: t('innovation.impact.ai.value'), desc: t('innovation.impact.ai.desc') }
            ].map((item, index) => (
              <GlareCard key={index} className="text-center">
                <span className="material-symbols-outlined text-3xl text-white/30 mb-4">{item.icon}</span>
                <h3 className="section-title mb-4">{item.title}</h3>
                <p className="text-3xl font-bold text-white mb-2 font-mono">{item.value}</p>
                <p className="section-desc">{item.desc}</p>
              </GlareCard>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - 点阵纹理背景 */}
      <section className="relative z-10 py-32 px-6 md:px-20 bg-dot-texture">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={staggerContainer}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div variants={textReveal} className="section-label justify-center flex mb-6">EXPERIENCE</motion.div>
          <motion.h2 variants={textReveal} className="text-4xl md:text-5xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
            {t('innovation.cta.title')}
          </motion.h2>
          <motion.p variants={textReveal} className="text-gray-500 text-lg font-mono mb-12">
            {t('innovation.cta.subtitle')}
          </motion.p>
          <motion.div variants={textReveal} className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/products" className="section-cta group">
              <span>{t('innovation.cta.products')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/contact" className="section-cta group border-white/10 hover:border-white/30 hover:bg-transparent hover:text-white">
              <span>{t('innovation.cta.contact')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  )
}

export default Innovation
